import { AfterContentInit, Component, ContentChild, ContentChildren, EventEmitter, Input, OnDestroy, Optional, Output, QueryList, Self, TemplateRef, ViewChild, ViewEncapsulation } from "@angular/core"
import { MatDialog } from "@angular/material/dialog"
import { MatPaginator, MatPaginatorIntl, PageEvent } from "@angular/material/paginator"
import { MatSort, Sort } from "@angular/material/sort"
import { MatColumnDef, MatHeaderRowDef, MatRowDef, MatTable, MatFooterRowDef } from "@angular/material/table"
import { DaoBase } from "./daos/dao-base"
import { StaticArrayDao } from "./daos/array-dao-base"
import { ApiModel, TrGlobalFilterResult, TrTableOnDeleteClick, TrTableOnEditClick } from "./models/models"
import { debounce } from "lodash"
import { Subscription } from "rxjs"
import { TrCustomizableColumnsComponent } from "./tr-customizable-columns/tr-customizable-columns.component"
import { TrColumnInfo, TrCustomizableColumnData } from "./tr-customizable-columns/tr-customizable-columns.model"
import { TrTableSelectionModel } from "./tr-table-selection"

@Component({
  selector: "tr-table",
  styleUrls: ["tr-table.component.scss"],
  templateUrl: "tr-table.component.html",
  encapsulation: ViewEncapsulation.None,
})
export class TrTableComponent<T extends ApiModel> implements AfterContentInit, OnDestroy {
  @Input() public dao: DaoBase<T>
  @Input() public columns: string[] = []
  @Input() public footerColumns: string[] = []
  @Input() public customizableColumns: TrColumnInfo[]
  @Input() public selectAll = false
  @Input() public sort = true
  @Input() public pageable = true
  @Input() public searchable = true
  @Input() public pageLimit = 20
  @Input() public sticky = true
  @Input() public selectionModel: TrTableSelectionModel
  @Input() public selectOnAllRows = true
  @Input() public showFirstLastButtons = true
  @Input() public itemsNotLoadedMessage = "No items found."
  @Input() public emptyFilteredItemsMessage = "No Search Results Found."
  @Input() public emptyItemsMessage = "No items available."
  @Input() public staticData = false
  @Input() public set data(newData: T[]) {
    if (!this.dao) {
      this.dao = new StaticArrayDao<T>(newData || [])
    } else {
      (this.dao as StaticArrayDao<T>).staticData = newData
    }
    this.dao.initialize()
  }

  @Output() public onSelectAllClicked = new EventEmitter<boolean>()
  @Output() public onAdd = new EventEmitter<void>()
  @Output() public onEdit = new EventEmitter<TrTableOnEditClick<T>>()
  @Output() public onDelete = new EventEmitter<TrTableOnDeleteClick<T>>()
  @Output() public dataChange = new EventEmitter<T[]>()
  @Output() public onRowClick = new EventEmitter<T>()
  @Output() public onCustomizeColumnsChange = new EventEmitter<string[]>()

  @ViewChild(MatTable, { read: MatTable, static: true}) public table: MatTable<T>
  @ViewChild(MatPaginator, { read: MatPaginator, static: true}) public paginator: MatPaginator
  @ViewChild(MatRowDef, { read: MatRowDef, static: true }) public rowDef: MatRowDef<T>
  @ViewChild(MatFooterRowDef, { read: MatFooterRowDef, static: true }) public footerDef: MatFooterRowDef
  @ViewChild(MatHeaderRowDef, { read: MatHeaderRowDef, static: true }) public headerRowDef: MatHeaderRowDef

  @ContentChildren(MatHeaderRowDef) public headerRowDefs: QueryList<MatHeaderRowDef>
  @ContentChildren(MatRowDef) public rowDefs: QueryList<MatRowDef<T>>
  @ContentChildren(MatFooterRowDef) public footerRowDefs: QueryList<MatFooterRowDef>
  @ContentChildren(MatColumnDef) public columnDefs: QueryList<MatColumnDef>
  @ContentChild(TemplateRef, { read: TemplateRef, static: true }) public globalFilterModal: TemplateRef<any>

  public filterChange = new EventEmitter<void>()
  public pageChange = new EventEmitter<void>()
  public filterColumns: string[] = []
  public displayedFilterColumns: string[] = []
  public isEmpty = false
  public isEmptyFilter = false
  public debounceOnSearch = debounce(
    async filterValue => await this.onSearch(filterValue),
    250,
    {},
  )

  private subscriptions: Subscription[] = []

  constructor(
    @Optional() @Self() public matSort: MatSort,
    public paginatorService: MatPaginatorIntl,
    public dialog: MatDialog,
  ) {}

  public async ngAfterContentInit(): Promise<void> {
    this.setupDefaults()
    this.populateColumns()
    this.setupSubscriptions()
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe())
    this.subscriptions = []
  }

  public async openColumnCustomizer(event: MouseEvent): Promise<void> {
    event.stopImmediatePropagation()
    const dialogRef = this.dialog.open<TrCustomizableColumnsComponent, TrCustomizableColumnData>(
      TrCustomizableColumnsComponent,
      {
        data: {
          columns: this.customizableColumns,
          currentColumns: this.columns,
        },
      },
    )
    const newColumns = (await dialogRef.afterClosed().toPromise()) as string[]
    if (newColumns) {
      this.columns = newColumns
      this.dao.columnsToFilter = newColumns.filter(v => v !== "actions")
      this.onCustomizeColumnsChange.emit(newColumns)
    }
  }

  public onClearAll(): void {
    this.dao.filterDefaults = {}
    this.onSearch("")
  }

  public async openGlobalFilter(event: MouseEvent): Promise<void> {
    if (this.globalFilterModal) {
      event.stopImmediatePropagation()

    }
    const dialogRef = this.dialog.open(this.globalFilterModal, { data: { dao: this.dao }})

    const result: TrGlobalFilterResult = await dialogRef.afterClosed().toPromise()
    if (result) {
      this.dao.globalFilter = result
      this.dao.updateDataFromFilter()
    }
  }

  public async addItem(value: T, addItemOnServer?: (value: T) => Promise<any>): Promise<void> {
    if (this.selectionModel) {
      this.selectionModel.clear()
    }

    this.dao.addItem(value, addItemOnServer)
  }

  public async updateItem(value: T, updateItemOnServer?: (value: T) => Promise<any>): Promise<void> {
    if (this.selectionModel) {
      this.selectionModel.clear()
    }

    this.dao.updateItem(value, updateItemOnServer)
  }

  public async deleteItem(id: string, deleteItemOnServer: () => Promise<void>): Promise<void> {
    if (this.selectionModel) {
      this.selectionModel.clear()
    }
    await this.dao.deleteItem(id, deleteItemOnServer)
  }

  public async deleteItems(ids: string[], deleteItemOnServer: () => Promise<void>): Promise<void> {
    if (this.selectionModel) {
      this.selectionModel.clear()
    }
    await this.dao.deleteItems(ids, deleteItemOnServer)
  }

  public rowClicked(row: T): void {
    this.onRowClick.emit(row)
    if (this.selectOnAllRows && this.selectionModel) {
      this.selectionModel.toggleSelection(row._id)
    }
  }

  public onDeleteClick(event: TrTableOnDeleteClick<T>): void {
    event.mouseEvent.stopImmediatePropagation()
    this.onDelete.emit(event)
  }

  public onEditClick(event: TrTableOnEditClick<T>): void {
    event.mouseEvent.stopImmediatePropagation()
    this.onEdit.emit(event)
  }

  public toggleAllSelections(): void {
    this.selectionModel.toggleAllSelections()
    const isAllSelected: boolean = this.dao.getPotentialSelections().length > 0
                                && this.selectionModel.selected.length === this.dao.getPotentialSelections().length
    this.onSelectAllClicked.emit(isAllSelected)
  }

  public async onSearch(filterValue: string): Promise<void> {
    // this.dao.columnsToFilter = this.columns.filter(v => v !== "actions")
    this.dao.search(filterValue)
  }

  public addColumn(columns: string[], column: string, inFront = false): string[] {
    if (columns.includes(column)) {
      return columns
    }

    inFront ? columns.unshift(column) : columns.push(column)
    return columns
  }

  public removeColumn(columns: string[], column: string): string[] {
    return columns.filter(c => c !== column)
  }

  public getColumns(): string[] {
    const frontColumns = []
    const endColumns = []

    if (this.selectionModel) {
      frontColumns.unshift("select")
    }

    if (this.onEdit.observers.length > 0 || this.onDelete.observers.length > 0) {
      endColumns.push("actions")
    }

    return [
      ...frontColumns,
      ...this.columns,
      ...endColumns,
    ]
  }

  // Helper Methods

  private populateColumns(): void {
    this.columnDefs.forEach(columnDef => this.table.addColumnDef(columnDef))
    // Allow the ability to override row definitions.
    if (this.rowDefs.length > 0) {
      this.table.removeRowDef(this.rowDef)
    }
    this.rowDefs.forEach(rowDef => this.table.addRowDef(rowDef))

    if (this.footerRowDefs.length > 0) {
      this.table.removeFooterRowDef(this.footerDef)
    }
    this.footerRowDefs.forEach(footerRef => this.table.addFooterRowDef(footerRef))

    // Allow the ability to override header definitions.
    if (this.headerRowDefs.length > 0) {
      this.table.removeHeaderRowDef(this.headerRowDef)
    }
    this.headerRowDefs.forEach(headerRowDef => this.table.addHeaderRowDef(headerRowDef))
  }

  private setupDefaults(): void {
    this.staticData = this.staticData || !!this.data
    if (this.staticData) {
      this.sort = false
      this.pageable = false
      this.searchable = false
      this.staticData = false

      if (this.data && !this.dao) {
        this.dao = new StaticArrayDao<T>(this.data)
      }
    }

    this.displayedFilterColumns = this.columns

    if (!this.dao.options) {
      this.dao.options = {}
    }

    if (this.matSort && this.sort) {
      this.dao.options.sortDirection = this.matSort.direction
      this.dao.options.sortField = this.matSort.active
    }

    if (this.pageable) {
      this.dao.options.limit = this.pageLimit
      this.dao.page.pageSize = this.pageLimit
    }

    this.dao.columnsToFilter = this.columns.filter(v => v !== "actions")
  }

  private setupSubscriptions(): void {
    const sortChangeSubscription = this.setupSort()
    const pageChangeSubscription = this.setupPaging()
    const currentDataChangedSubscription = this.setupCurrentDataChanged()
    const totalDataChangedSubscription = this.setupTotalDataChanged()

    this.subscriptions = [
      sortChangeSubscription,
      pageChangeSubscription,
      currentDataChangedSubscription,
      totalDataChangedSubscription,
    ].filter(s => !!s)
  }

  private setupCurrentDataChanged(): Subscription {
    return this.dao.currentData.subscribe(data => {
      this.isEmpty = data.length === 0 && !this.dao.filter
      this.isEmptyFilter = data.length === 0 && !!this.dao.filter
    })
  }

  private setupTotalDataChanged(): Subscription {
    return this.dao.totalData.subscribe(data => {
      this.isEmpty = data.length === 0 && !this.dao.filter
    })
  }

  private setupPaging(): Subscription {
    if (this.pageable) {
      return this.paginator.page.subscribe(async (page: PageEvent) => {
        // this.dao.columnsToFilter = this.columns.filter(v => v !== "actions")
        await this.dao.changePage(page, this.pageLimit)
      })
    }

    return null
  }

  private setupSort(): Subscription {
    if (this.sort && this.matSort) {
      return this.matSort.sortChange.subscribe(async (sort: Sort) => {
        // this.dao.columnsToFilter = this.columns.filter(v => v !== "actions")
        await this.dao.sort(sort, this.pageLimit)
      })
    }

    return null
  }
}
